import { HideAllLinesCommand } from "slotworks/commands/lines/hide-all-lines-command";
import { ShowLinesCommand } from "slotworks/commands/lines/show-lines-command";
import DimSymbolsCommand from "slotworks/commands/symbols/dim-symbols-command";
import { DimType } from "slotworks/commands/symbols/dim-symbols-command";
import { PlaySymbolSoundCommand } from "slotworks/commands/symbols/play-symbol-sound-command";
import { WinningSymbolsCommand } from "slotworks/commands/symbols/winning-symbols-command";
import Components from "slotworks/components/components";
import GameState from "slotworks/model/game-state";
import Record from "slotworks/model/gameplay/records/record";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import WaysResult from "slotworks/model/gameplay/records/results/ways-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import SlotDefinition from "slotworks/model/slot-definition";
import State from "slotworks/state-machine/states/state";
import { reject } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contracts/contract";
import { parallel } from "slotworks/utils/race-utils";
import Timer from "slotworks/utils/timer";

export default class LoopWins extends State {

    public static DELAY: number = 0;
    public static DIM_SYMBOLS: boolean = false;

    protected recordCycleCount: Map<Record, number>;

    protected wins: SymbolWinResult[];
    protected currentWin: number;

    public onEnter() {
        if (!this.recordCycleCount) {
            this.recordCycleCount = new Map<Record, number>();
            GameState.onNewGame.add(() => this.recordCycleCount.clear());
        }

        this.startLoop().then(() => this.complete());
    }

    public onSkip() {
        this.endLoop().then(() => this.skip());
    }

    public onExit() {
        Components.matrix.resetSymbols();
    }

    public startLoop(): Contract<void> {
        if (GameState.currentRecordHasSymbolWinResults()) {

            const record = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;

            if (this.recordCycleCount.has(record)) {
                this.recordCycleCount.set(record, this.recordCycleCount.get(record) + 1);
            } else {
                this.recordCycleCount.set(record, 0);
            }

            this.wins = record.getResultsOfType(SymbolWinResult) as SymbolWinResult[];

            this.currentWin = 0;

            return new Contract<void>((resolve) => {
                this.nextWin(resolve);
            });
        } else {
            return Contract.empty();
        }
    }

    public endLoop(): Contract<void> {
        return new Contract<void>((resolve) => {
            this.wins = null;
            Components.matrix.resetSymbols();
            HideAllLinesCommand().execute();
            Components.celebrateWin.hide();
            resolve(null);
        });
    }

    protected nextWin(onComplete: Function) {

        const raceFuncs: Array<() => Contract<any>> = [];

        const win = this.wins[this.currentWin];
        const symbol = SlotDefinition.getSymbolDefinition(win.symbolType);
        const symbolName = symbol ? symbol.name : "";

        if (win instanceof LineResult) {
            const lineDef = SlotDefinition.lines.values[win.line - 1];
            const middleSymbolRow = lineDef[Math.floor(lineDef.length * 0.5)].y + 1;

            ShowLinesCommand([win.line]).execute();
            raceFuncs.push(() => Components.celebrateWin.lineWin(win, symbolName, middleSymbolRow));
        } else if (win instanceof WaysResult) {
            raceFuncs.push(() => Components.celebrateWin.waysWin(win, symbolName));
        } else if (win.cashWon > 0) {
            raceFuncs.push(() => Components.celebrateWin.lineWin(win as LineResult, symbolName));
        }

        raceFuncs.push(() => this.showWin(win));

        parallel(raceFuncs).then(
            () => {
                Timer.setTimeout(() => {
                    if (!this.wins) {
                        return;
                    }

                    Components.matrix.resetSymbols();
                    HideAllLinesCommand().execute();

                    this.currentWin++;
                    if (this.currentWin < this.wins.length) {
                        if (this.nextWin) {
                            this.nextWin(onComplete);
                        }
                    } else {
                        onComplete();
                    }
                }, LoopWins.DELAY);
            }
        );
    }

    protected showWin(win: SymbolWinResult): Contract<void> {
        const gameplay = GameState.getCurrentGame();
        const record = gameplay.getCurrentRecord() as SpinRecord;

        const winningSymbols = Components.matrix.getSymbolsFromPositions(win.positions);

        const currentCycle = this.recordCycleCount.get(record);

        PlaySymbolSoundCommand(win.symbolType, win.matches.toString(), currentCycle.toString());

        const remainingSymbols = reject(Components.matrix.getBaseGridSymbols(), winningSymbols);

        const hiddenSymbols = Components.matrix.getHiddenSymbols();
        DimSymbolsCommand(remainingSymbols.concat(hiddenSymbols), DimType.Cycle).execute();

        if (!winningSymbols.length) {
            return Contract.getTimeoutContract(1000);
        }

        return WinningSymbolsCommand(winningSymbols, currentCycle > 0);
    }
}
